import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'
import { FileText, Download, TrendingUp, Users, Clock, DollarSign, FileDown, Filter, ChevronDown } from 'lucide-react'
import { format } from 'date-fns'
import { TutorBalanceReport } from '../components/TutorBalanceReport'
import { hasAdminRights } from '../lib/roles'

interface ReportStats {
  totalRevenue: number
  totalSessions: number
  totalMinutes: number
  activeStudents: number
  activeTutors: number
  averageSessionLength: number
}

export function Reports() {
  const { profile } = useAuth()
  const isAdmin = hasAdminRights(profile?.role)

  const [stats, setStats] = useState<ReportStats>({
    totalRevenue: 0,
    totalSessions: 0,
    totalMinutes: 0,
    activeStudents: 0,
    activeTutors: 0,
    averageSessionLength: 0
  })
  const [loading, setLoading] = useState(true)
  const [showColumnFilter, setShowColumnFilter] = useState(false)
  const [visibleStats, setVisibleStats] = useState({
    totalRevenue: true,
    totalSessions: true,
    totalMinutes: true,
    activeStudents: true,
    activeTutors: true,
    averageSession: true
  })
  const [dateRange, setDateRange] = useState({
    start: format(new Date(new Date().setDate(new Date().getDate() - 30)), 'yyyy-MM-dd'),
    end: format(new Date(), 'yyyy-MM-dd')
  })

  useEffect(() => {
    fetchReportData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, profile])

  const fetchReportData = async () => {
    try {
      setLoading(true)

      // Fetch revenue data — money, admins only.
      let topupsData: { amount_usd: number }[] | null = null
      if (isAdmin) {
        const res = await supabase
          .from('topups')
          .select('amount_usd')
          .gte('created_at', dateRange.start)
          .lte('created_at', dateRange.end)
        topupsData = res.data
      }

      // Fetch sessions data
      const { data: sessionsData } = await supabase
        .from('sessions')
        .select('minutes, student_id, tutor_id')
        .gte('started_at', dateRange.start)
        .lte('started_at', dateRange.end)

      // Fetch active students
      const { data: studentsData } = await supabase
        .from('students')
        .select('id')
        .eq('status', 'active')

      // Fetch active tutors
      const { data: tutorsData } = await supabase
        .from('tutors')
        .select('id')
        .eq('active', true)

      const totalRevenue = topupsData?.reduce((sum, t) => sum + (t.amount_usd || 0), 0) || 0
      const totalMinutes = sessionsData?.reduce((sum, s) => sum + (s.minutes || 0), 0) || 0
      const totalSessions = sessionsData?.length || 0
      const averageSessionLength = totalSessions > 0 ? Math.round(totalMinutes / totalSessions) : 0

      setStats({
        totalRevenue,
        totalSessions,
        totalMinutes,
        activeStudents: studentsData?.length || 0,
        activeTutors: tutorsData?.length || 0,
        averageSessionLength
      })
    } catch (error) {
      console.error('Error fetching report data:', error)
    } finally {
      setLoading(false)
    }
  }

  const exportStatsToCSV = () => {
    const csvData: any = {}

    if (isAdmin && visibleStats.totalRevenue) {
      csvData['Total Revenue'] = `$${stats.totalRevenue.toFixed(2)}`
    }
    if (visibleStats.totalSessions) {
      csvData['Total Sessions'] = stats.totalSessions
    }
    if (visibleStats.totalMinutes) {
      csvData['Total Minutes'] = stats.totalMinutes
    }
    if (visibleStats.activeStudents) {
      csvData['Active Students'] = stats.activeStudents
    }
    if (visibleStats.activeTutors) {
      csvData['Active Tutors'] = stats.activeTutors
    }
    if (visibleStats.averageSession) {
      csvData['Average Session Length'] = `${stats.averageSessionLength} minutes`
    }
    csvData['Date Range'] = `${dateRange.start} to ${dateRange.end}`

    const headers = Object.keys(csvData)
    const values = Object.values(csvData)

    const csvContent = [
      headers.join(','),
      values.map(v => `"${v}"`).join(',')
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `report_stats_${format(new Date(), 'yyyy-MM-dd')}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    window.URL.revokeObjectURL(url)
  }

  const exportToCSV = async (reportType: string) => {
    let csvContent = ''
    let filename = ''

    switch (reportType) {
      case 'sessions':
        const { data: sessions } = await supabase
          .from('sessions')
          .select('*')
          .gte('started_at', dateRange.start)
          .lte('started_at', dateRange.end)

        csvContent = 'Date,Student ID,Tutor ID,Duration (min),Notes\n'
        sessions?.forEach(s => {
          csvContent += `${s.started_at},${s.student_id},${s.tutor_id},${s.minutes},"${s.notes || ''}"\n`
        })
        filename = `sessions_${dateRange.start}_to_${dateRange.end}.csv`
        break

      case 'revenue':
        const { data: topups } = await supabase
          .from('topups')
          .select('*')
          .gte('created_at', dateRange.start)
          .lte('created_at', dateRange.end)

        csvContent = 'Date,Student ID,Amount,Method,Minutes Added,Note\n'
        topups?.forEach(t => {
          csvContent += `${t.created_at},${t.student_id},${t.amount_usd},${t.method},${t.minutes_added},"${t.note || ''}"\n`
        })
        filename = `revenue_${dateRange.start}_to_${dateRange.end}.csv`
        break

      default:
        return
    }

    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    a.click()
    window.URL.revokeObjectURL(url)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-400"></div>
      </div>
    )
  }

  return (
    <div>
      <div className="mb-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center mb-4">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Reports</h1>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={exportStatsToCSV}
              className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              <FileDown className="h-5 w-5 mr-2" />
              Export Stats CSV
            </button>
            <div className="relative">
              <button
                onClick={() => setShowColumnFilter(!showColumnFilter)}
                className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                <Filter className="h-5 w-5 mr-2" />
                Stats
                <ChevronDown className="h-4 w-4 ml-1" />
              </button>
              {showColumnFilter && (
                <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-surface-100 rounded-lg shadow-lg z-10 p-3">
                  <div className="space-y-2">
                    {Object.entries(visibleStats)
                      .filter(([key]) => isAdmin || key !== 'totalRevenue')
                      .map(([key, value]) => (
                      <label key={key} className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={value}
                          onChange={(e) => setVisibleStats({ ...visibleStats, [key]: e.target.checked })}
                          className="w-4 h-4 text-brand-600 rounded focus:ring-brand-500"
                        />
                        <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                          {key === 'totalRevenue' ? 'Total Revenue' :
                           key === 'totalSessions' ? 'Total Sessions' :
                           key === 'totalMinutes' ? 'Total Minutes' :
                           key === 'activeStudents' ? 'Active Students' :
                           key === 'activeTutors' ? 'Active Tutors' :
                           key === 'averageSession' ? 'Average Session' : key}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Date Range Selector */}
        <div className="bg-white dark:bg-surface-100 rounded-lg shadow p-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300 shrink-0">Date Range:</label>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-3">
            <input
              type="date"
              value={dateRange.start}
              onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
              className="w-full sm:w-auto px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 bg-white dark:bg-surface-0 text-gray-900 dark:text-white"
            />
            <span className="text-gray-500 text-center sm:text-left">to</span>
            <input
              type="date"
              value={dateRange.end}
              onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
              className="w-full sm:w-auto px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 bg-white dark:bg-surface-0 text-gray-900 dark:text-white"
            />
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {isAdmin && visibleStats.totalRevenue && (
          <div className="bg-white dark:bg-surface-100 rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <DollarSign className="h-8 w-8 text-green-500" />
              <TrendingUp className="h-5 w-5 text-green-500" />
            </div>
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Revenue</h3>
            <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
              ${stats.totalRevenue.toFixed(2)}
            </p>
          </div>
        )}

        {visibleStats.totalSessions && (
          <div className="bg-white dark:bg-surface-100 rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <FileText className="h-8 w-8 text-blue-500" />
              <span className="text-sm text-gray-500 dark:text-gray-400">{stats.totalSessions}</span>
            </div>
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Sessions</h3>
            <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{stats.totalSessions}</p>
          </div>
        )}

        {visibleStats.totalMinutes && (
          <div className="bg-white dark:bg-surface-100 rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <Clock className="h-8 w-8 text-purple-500" />
              <span className="text-sm text-gray-500 dark:text-gray-400">{stats.averageSessionLength} min avg</span>
            </div>
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Minutes</h3>
            <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
              {stats.totalMinutes.toLocaleString()}
            </p>
          </div>
        )}

        {visibleStats.activeStudents && (
          <div className="bg-white dark:bg-surface-100 rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <Users className="h-8 w-8 text-orange-500" />
            </div>
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Active Students</h3>
            <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{stats.activeStudents}</p>
          </div>
        )}

        {visibleStats.activeTutors && (
          <div className="bg-white dark:bg-surface-100 rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <Users className="h-8 w-8 text-indigo-500" />
            </div>
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Active Tutors</h3>
            <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{stats.activeTutors}</p>
          </div>
        )}

        {visibleStats.averageSession && (
          <div className="bg-white dark:bg-surface-100 rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <Clock className="h-8 w-8 text-teal-500" />
            </div>
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Avg Session Length</h3>
            <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
              {stats.averageSessionLength} min
            </p>
          </div>
        )}
      </div>

      {isAdmin && <TutorBalanceReport />}

      {/* Export Options */}
      <div className="bg-white dark:bg-surface-100 rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Export Reports</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button
            onClick={() => exportToCSV('sessions')}
            className="flex items-center justify-center px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-surface-0 transition-colors"
          >
            <Download className="h-5 w-5 mr-2 text-gray-500 dark:text-gray-400" />
            <span className="text-gray-700 dark:text-gray-300">Export Sessions Report</span>
          </button>
          {isAdmin && (
            <button
              onClick={() => exportToCSV('revenue')}
              className="flex items-center justify-center px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-surface-0 transition-colors"
            >
              <Download className="h-5 w-5 mr-2 text-gray-500 dark:text-gray-400" />
              <span className="text-gray-700 dark:text-gray-300">Export Revenue Report</span>
            </button>
          )}
        </div>
      </div>

      {/* Summary */}
      <div className="mt-8 bg-white dark:bg-surface-100 rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Report Summary</h2>
        <div className="prose dark:prose-invert max-w-none">
          <p className="text-gray-600 dark:text-gray-400">
            During the period from <strong>{format(new Date(dateRange.start), 'MMM d, yyyy')}</strong> to{' '}
            <strong>{format(new Date(dateRange.end), 'MMM d, yyyy')}</strong>:
          </p>
          <ul className="mt-4 space-y-2 text-gray-600 dark:text-gray-400">
            {isAdmin && <li>Generated <strong>${stats.totalRevenue.toFixed(2)}</strong> in revenue</li>}
            <li>Conducted <strong>{stats.totalSessions}</strong> tutoring sessions</li>
            <li>Delivered <strong>{stats.totalMinutes.toLocaleString()}</strong> minutes of instruction</li>
            <li>Served <strong>{stats.activeStudents}</strong> active students</li>
            <li>Employed <strong>{stats.activeTutors}</strong> active tutors</li>
          </ul>
        </div>
      </div>
    </div>
  )
}